import { useEffect, useRef, useState } from 'react'
import { Link, useNavigate, useParams } from 'react-router-dom'

import { api } from '../api'
import { useAktion, useLaden } from '../hooks'
import Entscheidungskarte, {
  artName,
  blattAktiv,
  blockName,
  entscheidungspunkte,
  wertText,
  type Antwort,
  type Entscheidungspunkt,
  type Entwurf,
} from '../komponenten/Entscheidungskarte'
import Meldung from '../komponenten/Meldung'
import { datum } from '../format'
import { ENTSCHEIDUNGART } from '../texte'
import type { ExcelArt, ExcelBlatt, ExcelImport } from '../typen'

/**
 * Excel-Import mit Plan: die Datei ist gelesen, aber noch nichts geschrieben. Je Blatt steht, was die
 * Anwendung erkannt hat (die KI hat es geprüft), die ersten Zeilen, Warnungen und die Kopplungen der
 * Summenblöcke. Die Nutzerin ändert Art, Gruppe, Kopplung, nimmt Blätter heraus - und führt aus.
 * Danach steht das Protokoll mit den Abgleichen: die Excel sagt, das System sagt.
 */
export default function ExcelImportSeite() {
  const { id } = useParams()
  const navigate = useNavigate()
  const [entwurf, setEntwurf] = useState<Entwurf>({})
  // Die Schlüssel der beantworteten Entscheidungspunkte - bleiben über ein Neu-Lesen hinweg bestehen,
  // sonst würde dieselbe Frage nach jeder Antwort, die ein Lesen auslöst, noch einmal gestellt.
  const [beantwortet, setBeantwortet] = useState<Set<string>>(new Set())
  const { fehler, setFehler, arbeitet, ausfuehren } = useAktion()
  // Jede je gestellte Frage bleibt gezählt, auch wenn sie nach dem Neu-Lesen verschwindet - sonst
  // spränge „x von y" hin und her (Befund 18.09.2026).
  const gestellt = useRef<Set<string>>(new Set())
  // Reihenfolgeschutz: nur die Antwort des letzten PUT darf den Plan setzen. Zwei schnelle Antworten
  // überholten sich sonst, und der ältere Plan überschrieb den jüngeren.
  const planLauf = useRef(0)

  /** Der Entwurf, wie ihn ein frisch gelesener Plan vorgibt. */
  function entwurfAus(d: ExcelImport): Entwurf {
    const e: Entwurf = {}
    for (const b of d.plan.blaetter ?? []) {
      const kopplungen: Record<string, string> = {}
      for (const [block, nummern] of Object.entries(b.kopplungen ?? b.kladde?.kopplungen_vorschlag ?? {})) kopplungen[block] = nummern.join(', ')
      e[b.blatt] = { art: b.art, gruppe: b.gruppe, aktiv: b.aktiv, kopplungen }
    }
    return e
  }

  /** Den Entwurf aus dem Plan füllen - nach jedem Lesen, sonst zeigt die Karte alte Häkchen. */
  function entwurfSetzen(d: ExcelImport) {
    setEntwurf(entwurfAus(d))
  }

  const { daten, setDaten, fehler: ladeFehler, neuLaden } = useLaden<ExcelImport>(() => api.excelImport(Number(id)), [id], {
    wenn: !!id,
    onGeladen: entwurfSetzen,
  })
  // Der Server liest den Plan im Hintergrund: solange er nicht bereit ist, alle drei Sekunden nachsehen.
  useEffect(() => {
    if (!daten || daten.bereit) return
    const t = setTimeout(neuLaden, 3000)
    return () => clearTimeout(t)
  }, [daten, neuLaden])

  /** Plan und Entwurf zusammen setzen - nach neu lesen und nach dem Ausführen. */
  function uebernehmen(d: ExcelImport) {
    setDaten(d)
    entwurfSetzen(d)
  }

  /** Der Entwurf, wie ihn das PUT erwartet: je Blatt Art, Gruppe, Häkchen und die Kopplungen als Nummernlisten. */
  /** Der Entwurf als Vorgaben für den Server. Ein PUT mit Kopplungen bestätigt sie - beim bloßen Neu-Lesen
   *  gehen deshalb nur die Blöcke mit, die die Nutzerin geändert hat; erst „So importieren" bestätigt alle
   *  (sonst wären nach einem Neu-Lesen sämtliche Vorschläge aller Blätter still bestätigt). */
  function entwurfAlsBlaetter(d: ExcelImport, alleKopplungen: boolean, stand: Entwurf = entwurf) {
    return d.plan.blaetter.map((b) => {
      const e = stand[b.blatt]
      const vorschlag = b.kopplungen ?? b.kladde?.kopplungen_vorschlag ?? {}
      const kopplungen: Record<string, string[]> = {}
      for (const [block, text] of Object.entries(e?.kopplungen ?? {})) {
        const nummern = text.split(/[,;\s]+/).filter(Boolean)
        const geaendert = nummern.join(',') !== (vorschlag[block] ?? []).join(',')
        if (nummern.length > 0 && (alleKopplungen || geaendert)) kopplungen[block] = nummern
      }
      return { blatt: b.blatt, art: e?.art ?? b.art, gruppe: e?.gruppe ?? b.gruppe, aktiv: e?.aktiv ?? b.aktiv, kopplungen }
    })
  }

  /**
   * Den Plan mit dem Entwurf neu lesen lassen. Die Laufnummer verwirft eine überholte Antwort: kommt
   * sie zurück, nachdem eine jüngere Anfrage losgeschickt wurde, wird sie weggeworfen (`null`) - der
   * Aufrufer merkt sich dann auch keine Antwort.
   */
  async function planAendern(blaetter: ReturnType<typeof entwurfAlsBlaetter>): Promise<ExcelImport | null> {
    if (!daten) return null
    const nummer = ++planLauf.current
    const neu = await api.excelPlanAendern(daten.id, blaetter)
    if (nummer !== planLauf.current) return null
    uebernehmen(neu)
    return neu
  }

  /** Mit dem Entwurf neu lesen - `stand` übergibt eine Antwort der Entscheidungskarte, bevor React den State gesetzt hat. */
  function neuLesen(stand: Entwurf = entwurf) {
    if (!daten) return
    void ausfuehren(async () => planAendern(entwurfAlsBlaetter(daten, false, stand)))
  }

  /**
   * Eine Antwort der Entscheidungskarte: den Entwurf der betroffenen Blätter ändern (bei gebündelten
   * Fragen sind es mehrere) und neu lesen, wo die Antwort das Lesen ändert (Art, Gruppe, Kopplung).
   * Gemerkt wird der Punkt erst, wenn das Lesen geglückt ist - scheitert das PUT, bleibt die Frage
   * stehen und die Meldung dazu. „Übernehmen" bei einer Kopplung ändert nichts - erst „So importieren"
   * bestätigt alle Vorschläge, wie sie im Feld stehen.
   */
  function antworten(punkt: Entscheidungspunkt, antwort: Antwort) {
    if (!daten) return
    if (antwort.typ === 'verwerfen') {
      if (!window.confirm(`Lauf ${daten.id} verwerfen? Die Datei bleibt als Lauf ${daten.plan.doppelt?.[0]?.lauf_id ?? '?'} geladen, dieser Plan wird verworfen.`)) return
      void ausfuehren(async () => {
        await api.verwerfen(daten.id)
        navigate('/importe')
      })
      return
    }
    const merken = () => setBeantwortet((alt) => new Set(alt).add(punkt.schluessel))
    if (punkt.typ === 'doppelt' || antwort.typ === 'weiter') {
      merken()
      return
    }
    let stand = entwurf
    for (const b of punkt.blaetter) {
      const alt = stand[b.blatt] ?? { art: b.art, gruppe: b.gruppe, aktiv: b.aktiv, kopplungen: {} }
      stand = {
        ...stand,
        [b.blatt]:
          antwort.typ === 'art'
            ? { ...alt, art: antwort.art }
            : antwort.typ === 'gruppe'
              ? { ...alt, gruppe: antwort.gruppe }
              : antwort.typ === 'aktiv'
                ? { ...alt, aktiv: true }
                : { ...alt, kopplungen: { ...alt.kopplungen, [punkt.typ === 'kopplung' ? punkt.block : '']: antwort.nummern } },
      }
    }
    setEntwurf(stand)
    // Das Häkchen ändert nur, ob das Blatt geschrieben wird - dafür muss der Server nichts neu lesen.
    if (antwort.typ === 'aktiv') {
      merken()
      return
    }
    void ausfuehren(async () => {
      const neu = await planAendern(entwurfAlsBlaetter(daten, false, stand))
      if (neu) merken()
    })
  }

  /**
   * Ausführen schreibt zuerst den Entwurf (PUT) und führt dann aus - in einem Schritt. Das PUT gilt dem
   * Server als Bestätigung der Kopplungsvorschläge; ohne sie antwortet er mit 409, und der Text davon
   * steht dann als Meldung über den Blättern (`fehler` aus useAktion). Hat sich der Plan beim Neulesen
   * geändert und dabei neue Fragen aufgeworfen, bricht der Lauf vor dem Schreiben ab.
   */
  function importieren() {
    if (!daten) return
    const aktive = daten.plan.blaetter.filter((b) => blattAktiv(b, entwurf))
    if (aktive.length === 0) {
      setFehler('Kein Blatt wird geladen - so schreibt der Import nichts.')
      return
    }
    if (kopplungLuecke.length > 0) {
      setFehler(`Ohne Fertigartikel lässt sich der Block nicht laden: ${kopplungLuecke.map((l) => `„${l.blatt}“ / „${blockName(l.block)}“`).join(', ')}.`)
      return
    }
    const vorschlaege = aktive.reduce((summe, b) => summe + (b.offene_vorschlaege?.length ?? 0), 0)
    const frage = [`${aktive.length} Blatt/Blätter jetzt in das System schreiben? Stammdaten, Bewegungen und Bestellungen werden angelegt.`]
    if (vorschlaege > 0) frage.push(`${vorschlaege} Kopplungsvorschl${vorschlaege === 1 ? 'ag wird' : 'äge werden'} damit bestätigt.`)
    if (!window.confirm(frage.join(' '))) return
    void ausfuehren(async () => {
      const neu = await planAendern(entwurfAlsBlaetter(daten, true))
      if (!neu) return
      // Der Server liest den Plan beim PUT neu: was er dabei anders sieht, kann neue Fragen aufwerfen.
      const nachher = entscheidungspunkte(neu, entwurfAus(neu)).filter((p) => !beantwortet.has(p.schluessel))
      if (nachher.length > 0) {
        throw new Error(
          `Der Plan hat sich beim Neulesen geändert - ${nachher.length === 1 ? '1 Entscheidung ist' : `${nachher.length} Entscheidungen sind`} offen.`,
        )
      }
      uebernehmen(await api.excelAusfuehren(daten.id))
    })
  }

  /** Den Lauf in den Chat tragen: der Assistent liest den Plan mit seinen Werkzeugen und führt erst nach Freigabe aus. */
  function besprechen() {
    if (!daten) return
    const nachricht = `Bitte prüfen Sie den Excel-Import-Lauf ${daten.id} („${daten.datei ?? daten.plan.datei}“) mit mir: Was wurde erkannt, welche Warnungen und Vorschläge gibt es, was würde gebucht? Ausführen erst nach meiner Freigabe.`
    navigate('/assistent', { state: { nachricht } })
  }

  if (ladeFehler && !daten) return <Meldung art="fehler" titel="Excel-Import nicht geladen">{ladeFehler}</Meldung>
  if (!daten) return <p className="hinweis">Lädt…</p>
  if (daten.fehler)
    return (
      <Meldung art="fehler" titel={`Excel-Import · ${daten.datei}`}>
        Die Datei ließ sich nicht lesen: {daten.fehler}
      </Meldung>
    )
  if (!daten.bereit)
    return (
      <section>
        <h2>Excel-Import · {daten.datei}</h2>
        <p className="hinweis">Die Datei wird gelesen, das kann einige Minuten dauern. Die Seite aktualisiert sich von selbst.</p>
      </section>
    )
  const ki = daten.ki
  const offen = daten.status === 'offen'
  const meldung = ladeFehler ?? fehler
  const punkte = offen ? entscheidungspunkte(daten, entwurf) : []
  for (const p of punkte) gestellt.current.add(p.schluessel)
  const offenePunkte = punkte.filter((p) => !beantwortet.has(p.schluessel)).length
  const aktiveBlaetter = daten.plan.blaetter.filter((b) => blattAktiv(b, entwurf))
  // Hartes Netz neben der Karte: ein Block ohne Produktnummer, für den im Entwurf keine Nummer steht,
  // sperrt das Schreiben - gleich wie weit die Karte ist. Sonst schriebe ein Blatt eine leere Kopplung.
  const kopplungLuecke = aktiveBlaetter.flatMap((b) =>
    (b.offene_vorschlaege ?? [])
      .filter((block) => !(entwurf[b.blatt]?.kopplungen?.[block] ?? '').trim())
      .map((block) => ({ blatt: b.blatt, block })),
  )
  const schreibenGesperrt = arbeitet || offenePunkte > 0 || aktiveBlaetter.length === 0 || kopplungLuecke.length > 0

  return (
    <section>
      <div className="leiste">
        <Link className="knopf" to="/importe">
          Zurück zur Liste
        </Link>
        <h2>Excel-Import · {daten.datei}</h2>
        <span className={`status ${daten.status === 'gebucht' ? 'gebucht' : 'offen'}`}>{daten.status === 'gebucht' ? 'übernommen' : 'Plan, noch nichts geschrieben'}</span>
      </div>
      {meldung && <Meldung art="fehler">{meldung}</Meldung>}
      {offen && (
        <Entscheidungskarte
          punkte={punkte}
          beantwortet={beantwortet}
          gesamt={gestellt.current.size}
          arten={daten.arten}
          gesperrt={arbeitet}
          keinBlattAktiv={aktiveBlaetter.length === 0}
          onAntwort={antworten}
        />
      )}
      {ki && (
        <Meldung art={ki.genutzt ? 'hinweis' : 'warnung'} titel={ki.genutzt ? `Die KI hat den Plan geprüft (${ki.modell})` : 'Plan ohne KI-Prüfung'}>
          {ki.genutzt && (ki.geaendert?.length ?? 0) > 0 && <div>Geändert oder ergänzt: {ki.geaendert?.join(', ')}.</div>}
          {ki.genutzt && (ki.geaendert?.length ?? 0) === 0 && <div>Die Zuordnung der Blätter bestätigt.</div>}
          {ki.hinweise?.map((h, i) => (
            <div key={i}>{h}</div>
          ))}
          {ki.fehler && <div>{ki.fehler}</div>}
        </Meldung>
      )}

      {(daten.protokoll?.uebersprungen?.length ?? 0) > 0 && (
        <Meldung art="warnung" titel={`${daten.protokoll?.uebersprungen?.length} Blätter wurden nicht geladen`}>
          {/* „übernommen" allein verschwieg, dass von fünf Blättern nur eines gebucht wurde (19.09.2026). */}
          {daten.protokoll?.uebersprungen?.map((u) => (
            <div key={u.blatt}>
              {u.blatt}: {u.grund}
            </div>
          ))}
        </Meldung>
      )}

      {daten.plan.blaetter.map((b) => (
        <BlattKarte
          key={b.blatt}
          blatt={b}
          arten={daten.arten}
          entwurf={entwurf[b.blatt]}
          bearbeitbar={offen && !arbeitet}
          onAendern={(neu) => setEntwurf((e) => ({ ...e, [b.blatt]: { ...e[b.blatt], ...neu } }))}
          protokoll={daten.protokoll?.blaetter.find((p) => p.blatt === b.blatt)}
        />
      ))}

      {offen && (
        <div className="leiste" style={{ marginTop: 12 }}>
          <button disabled={arbeitet} onClick={() => neuLesen()}>
            Mit diesen Vorgaben neu lesen
          </button>
          <button className="haupt" disabled={schreibenGesperrt} onClick={importieren}>
            {arbeitet ? 'Arbeitet…' : gestellt.current.size > 0 && !schreibenGesperrt ? 'Alles entschieden - So importieren' : 'So importieren'}
          </button>
          {offenePunkte > 0 && (
            <span className="entscheidungen-offen">
              {offenePunkte === 1 ? 'noch 1 Entscheidung offen' : `noch ${offenePunkte} Entscheidungen offen`} - oben beantworten
            </span>
          )}
          {offenePunkte === 0 && aktiveBlaetter.length === 0 && <span className="entscheidungen-offen">Kein Blatt wird geladen - so schreibt der Import nichts.</span>}
          {offenePunkte === 0 && aktiveBlaetter.length > 0 && kopplungLuecke.length > 0 && (
            <span className="entscheidungen-offen">
              {kopplungLuecke.length === 1 ? 'Ein Block' : `${kopplungLuecke.length} Blöcke`} ohne Fertigartikel: {kopplungLuecke.map((l) => `„${l.blatt}“ / „${blockName(l.block)}“`).join(', ')} - unten
              eintragen.
            </span>
          )}
          <button disabled={arbeitet} onClick={besprechen}>
            Mit dem Assistenten besprechen
          </button>
          <span className="hinweis">„Neu lesen“ zeigt, was geänderte Art, Gruppe oder Kopplungen bewirken. „So importieren“ übernimmt die Vorgaben und schreibt.</span>
        </div>
      )}
      {offen && <p className="hinweis">Sie können die Datei auch direkt im Chat anhängen (Büroklammer).</p>}
    </section>
  )
}

function BlattKarte({
  blatt,
  arten,
  entwurf,
  bearbeitbar,
  onAendern,
  protokoll,
}: {
  blatt: ExcelBlatt
  arten: Record<ExcelArt, string>
  entwurf?: Entwurf[string]
  bearbeitbar: boolean
  onAendern: (neu: Partial<Entwurf[string]>) => void
  protokoll?: ExcelImport['protokoll'] extends infer P ? (P extends { blaetter: (infer B)[] } ? B : never) : never
}) {
  const [offen, setOffen] = useState(false)
  const art = entwurf?.art ?? blatt.art
  const aktiv = entwurf?.aktiv ?? blatt.aktiv
  const k = blatt.kladde
  // Blöcke, deren Kopplung der Server nur vermutet - erst das Ausführen (PUT davor) bestätigt sie.
  const vorschlaege = blatt.offene_vorschlaege ?? []
  const zahlen: string[] = []
  if (k) zahlen.push(`${k.fertigartikel.length} Fertigartikel, ${k.materialien.length} Materialien, ${k.zaehlung.tageszeilen} Tage, ${k.zaehlung.inventuren} Inventuren, ${k.zaehlung.wareneingaenge} Wareneingänge, ${k.zeitraum.von ? datum(k.zeitraum.von) : '?'} bis ${k.zeitraum.bis ? datum(k.zeitraum.bis) : '?'}`)
  if (blatt.disposition) zahlen.push(`${blatt.disposition.materialien.length} Materialien, Stand ${blatt.disposition.stichtag ? datum(blatt.disposition.stichtag) : '?'}`)
  if (blatt.zaehleinheiten) {
    // Eine Monatsliste trägt Zählungen: die werden zur Inventur zum Monatsende (Stand aus dem Dateinamen).
    const gezaehlt = blatt.zaehleinheiten.gezaehlt ?? 0
    const einheiten = blatt.einheiten_uebernehmen === false ? 'Zähleinheiten bleiben vom neueren Stand' : `${blatt.zaehleinheiten.artikel.length} Artikel mit Zähleinheiten`
    zahlen.push(gezaehlt > 0 && art !== 'ignorieren' ? `${einheiten}, ${gezaehlt} Zählungen zur Inventur ${blatt.datenstand ? datum(blatt.datenstand) : 'ohne Stichtag'}` : einheiten)
  }
  if (blatt.auswertung) zahlen.push(`${blatt.auswertung.artikel.length} Artikel, MVN aus Stück und Tonnen`)
  if (blatt.planmengen) zahlen.push(`${blatt.planmengen.werte} Monatswerte für ${blatt.planmengen.fertigartikel.length} Fertigartikel (${blatt.planmengen.jahre.join(', ')})`)
  if ((blatt.artikel_neu?.length ?? 0) > 0) zahlen.push(`${blatt.artikel_neu?.length} Artikel neu`)
  // Alle Texte des Blatts hinter einem Aufklapper: was eine Handlung verlangt, fragt die Entscheidungskarte oben.
  const anzahlTexte = blatt.hinweise.length + blatt.warnungen.length
  // Ein Abzeichen je Datum: „aus Entscheidung vom 11.08.2026", die Einzelheiten im Tooltip.
  const gemerkt = new Map<string, string[]>()
  for (const e of blatt.entscheidungen ?? []) {
    const tag = e.seit ? datum(e.seit) : 'unbekanntem Tag'
    const block = e.art === 'kopplung' && e.block ? ` „${blockName(e.block)}“` : ''
    gemerkt.set(tag, [...(gemerkt.get(tag) ?? []), `${ENTSCHEIDUNGART[e.art]}${block}: ${wertText(e.wert) || '-'}${e.lauf_id ? ` (Lauf ${e.lauf_id})` : ''}`])
  }

  return (
    <div className={`karte${aktiv && art !== 'ignorieren' ? '' : ' eingeklappt'}`} style={{ marginTop: 10, opacity: aktiv && art !== 'ignorieren' ? 1 : 0.7 }}>
      <div className="leiste">
        <label>
          <input type="checkbox" checked={aktiv} disabled={!bearbeitbar} onChange={(e) => onAendern({ aktiv: e.target.checked })} /> <b>{blatt.blatt}</b>
        </label>
        <select value={art} disabled={!bearbeitbar} onChange={(e) => onAendern({ art: e.target.value as ExcelArt })} title={arten[art]}>
          {(Object.keys(arten) as ExcelArt[]).map((a) => (
            <option key={a} value={a} title={arten[a]}>
              {artName(arten[a])}
            </option>
          ))}
        </select>
        {(art === 'kladde' || art === 'disposition') && (
          <input type="text" value={entwurf?.gruppe ?? blatt.gruppe} disabled={!bearbeitbar} onChange={(e) => onAendern({ gruppe: e.target.value })} title="Gruppe (Blatt der Kladde)" style={{ width: 200 }} />
        )}
        {blatt.sicherheit === 'unsicher' && <span className="marke warnung">KI unsicher</span>}
        {blatt.aelterer_stand && !aktiv && <span className="marke warnung">abgewählt - älterer Stand</span>}
        {[...gemerkt.entries()].map(([tag, texte]) => (
          <span key={tag} className="marke gemerkt" title={texte.join('\n')}>
            aus Entscheidung vom {tag}
          </span>
        ))}
        <span className="hinweis">{blatt.zeilen} × {blatt.spalten}</span>
        <button onClick={() => setOffen((o) => !o)}>{offen ? 'Vorschau zu' : 'Vorschau'}</button>
      </div>
      <p className="hinweis" style={{ margin: '4px 0' }}>{arten[art]}{zahlen.length > 0 ? ` - ${zahlen.join('; ')}` : ''}</p>
      {k && Object.keys(entwurf?.kopplungen ?? {}).length > 0 && (
        <div style={{ margin: '6px 0' }}>
          <b>Blöcke ohne Produktnummer - welche Fertigartikel ziehen sie?</b>
          {Object.entries(entwurf?.kopplungen ?? {}).map(([block, text]) => (
            <div className="leiste" key={block} style={{ marginTop: 4 }}>
              <span style={{ minWidth: 260 }}>
                {blockName(block)}
                {vorschlaege.includes(block) && <span className="kopplung-vorschlag"> (Vorschlag)</span>}
              </span>
              <input type="text" value={text} disabled={!bearbeitbar} style={{ width: 260 }} onChange={(e) => onAendern({ kopplungen: { ...(entwurf?.kopplungen ?? {}), [block]: e.target.value } })} />
              <span className="hinweis">von {k.fertigartikel.map((f) => f.nummer).join(', ')}</span>
            </div>
          ))}
        </div>
      )}
      {anzahlTexte > 0 && (
        <details className="blatt-hinweise">
          <summary>Alle Hinweise ({anzahlTexte})</summary>
          {blatt.hinweise.length > 0 && (
            <Meldung art="hinweis" titel="Hinweise">
              <ul>
                {blatt.hinweise.map((h, i) => (
                  <li key={i}>{h}</li>
                ))}
              </ul>
            </Meldung>
          )}
          {blatt.warnungen.length > 0 && (
            <Meldung art="warnung" titel={`${blatt.warnungen.length} Warnung${blatt.warnungen.length === 1 ? '' : 'en'} beim Lesen`}>
              <ul>
                {blatt.warnungen.map((w, i) => (
                  <li key={i}>{w}</li>
                ))}
              </ul>
            </Meldung>
          )}
        </details>
      )}
      {offen && (
        <div className="tabellenrahmen" style={{ marginTop: 6 }}>
          <table>
            <tbody>
              {blatt.vorschau.map((z, i) => (
                <tr key={i}>
                  {z.map((v, j) => (
                    <td key={j} className="hinweis">
                      {v}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {protokoll && <Protokoll p={protokoll} bekannt={blatt.warnungen} />}
    </div>
  )
}

/** Die Zahlen des Protokolls in Worten - die Schlüssel kommen aus dem Importer. */
const ZAHLTITEL: Record<string, string> = {
  artikel_neu: 'Artikel neu',
  kopplungen: 'Kopplungen',
  bewegungen: 'Bewegungen',
  bewegungen_ersetzt: 'Bewegungen ersetzt',
  produktion: 'Tagesproduktion',
  tage_gerechnet: 'Tage neu gerechnet',
  bestellungen: 'Bestellungen',
  ersetzt: 'Bestellungen ersetzt',
  sperrungen: 'Sperrungen',
  stammdaten: 'Stammdaten geändert',
  anfangsbestaende: 'Anfangsbestände',
  produktionsplan: 'Wochenplan-Einträge',
  summen_gleich: 'Excel-Summen getroffen',
  einheiten: 'Zähleinheiten',
  zaehlungen: 'Zählungen übernommen',
  inventur_offen: 'Zählungen noch nicht gebucht (Inventur offen)',
  paletten_gesetzt: 'Palettengrößen',
  mvn_gesetzt: 'MVN gesetzt',
  artikel: 'Artikel',
  gesetzt: 'Planmengen',
}

function Protokoll({
  p,
  bekannt,
}: {
  p: { warnungen: string[]; abgleich?: { artikel: string; was: string; excel: number | string | null; excel_gesamt?: string | null; system: number | string | null; system_gesamt?: string | null }[]; [k: string]: unknown }
  bekannt: string[]
}) {
  const zahlen = Object.entries(p).filter(([k, v]) => typeof v === 'number' && k !== 'id')
  const abgleich = p.abgleich ?? []
  const geloest = Array.isArray(p.kopplung_geloest) ? (p.kopplung_geloest as string[]) : []
  const neueWarnungen = p.warnungen.filter((w) => !bekannt.includes(w))
  const leer = zahlen.length === 0 && abgleich.length === 0
  return (
    <Meldung art={leer ? 'hinweis' : 'gut'} titel={leer ? 'Nichts zu übernehmen' : 'Übernommen'}>
      {leer && 'Dieses Blatt liefert nur Abgleichswerte, und es gab nichts abzugleichen (keine Werte oder keine passenden Artikel).'}
      {zahlen.map(([k, v]) => `${ZAHLTITEL[k] ?? k.replace(/_/g, ' ')}: ${String(v)}`).join(' · ')}
      {geloest.length > 0 && (
        <p style={{ margin: '6px 0 0' }}>
          Kopplung nach der Excel gesetzt, Vermutetes gelöst: {geloest.join('; ')}
        </p>
      )}
      {abgleich.length > 0 && (
        <table style={{ marginTop: 6 }}>
          <thead>
            <tr>
              <th>Artikel</th>
              <th>Was</th>
              <th>Excel</th>
              <th>System</th>
            </tr>
          </thead>
          <tbody>
            {abgleich.map((a, i) => (
              <tr key={i}>
                <td>{a.artikel}</td>
                <td>{a.was}</td>
                <td>
                  {String(a.excel ?? '')}
                  {a.excel_gesamt ? ` / gesamt ${a.excel_gesamt}` : ''}
                </td>
                <td>
                  {a.system === null || a.system === undefined ? '' : String(a.system)}
                  {a.system_gesamt ? ` / gesamt ${a.system_gesamt}` : ''}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
      {neueWarnungen.length > 0 && (
        <ul>
          {neueWarnungen.slice(0, 20).map((w, i) => (
            <li key={i}>{w}</li>
          ))}
        </ul>
      )}
    </Meldung>
  )
}
